/**
 * Client HTTP de l'API. Toutes les requêtes portent le cookie de session
 * (`credentials: 'include'`) ; un 401 fait basculer l'app sur l'écran de login.
 */
import type {
  AlertChannel,
  Competitor,
  CompetitorAnalysisRun,
  CompetitorInput,
  CompetitorInsight,
  AlertChannelInput,
  DashboardStats,
  Draft,
  Hit,
  Keyword,
  KeywordInput,
  Platform,
  PlatformInput,
  PollRun,
  Post,
  PostMetrics,
  Site,
  SiteInput,
  SocialAccount,
  SocialAccountInput,
  SocialAsset,
  SocialPostDetail,
  SocialPostInput,
  Source,
  SourceInput,
  User,
} from '@vigie/shared';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'http://localhost:8788';

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'message' in payload
        ? String((payload as { message: unknown }).message)
        : `Erreur ${response.status}`;
    throw new ApiError(response.status, message);
  }
  return payload as T;
}

const body = (value: unknown): RequestInit => ({ body: JSON.stringify(value ?? {}) });

export interface PostWithMetrics {
  post: Post;
  metrics: PostMetrics;
  hitTitle: string;
  hitUrl: string;
  siteName: string;
}

export interface CollectionOutcome {
  pollRunId: string;
  status: string;
  itemsFetched: number;
  itemsNew: number;
  itemsKept: number;
  error: string | null;
}

export const api = {
  // --- Auth ---
  config: () => request<{ allowSignup: boolean }>('/auth/config'),
  me: () => request<{ user: User; allowSignup: boolean }>('/auth/me'),
  login: (email: string, password: string) =>
    request<{ user: User }>('/auth/login', { method: 'POST', ...body({ email, password }) }),
  signup: (email: string, password: string) =>
    request<{ user: User }>('/auth/signup', { method: 'POST', ...body({ email, password }) }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  // --- Configuration ---
  sites: () => request<Site[]>('/sites'),
  createSite: (input: SiteInput) => request<Site>('/sites', { method: 'POST', ...body(input) }),
  updateSite: (id: string, input: Partial<SiteInput>) =>
    request<Site>(`/sites/${id}`, { method: 'PATCH', ...body(input) }),
  deleteSite: (id: string) => request<{ ok: true }>(`/sites/${id}`, { method: 'DELETE' }),

  platforms: (siteId: string) => request<Platform[]>(`/sites/${siteId}/platforms`),
  createPlatform: (siteId: string, input: PlatformInput) =>
    request<Platform>(`/sites/${siteId}/platforms`, { method: 'POST', ...body(input) }),
  updatePlatform: (id: string, input: Partial<PlatformInput>) =>
    request<Platform>(`/platforms/${id}`, { method: 'PATCH', ...body(input) }),
  deletePlatform: (id: string) => request<{ ok: true }>(`/platforms/${id}`, { method: 'DELETE' }),

  sources: (platformId: string) => request<Source[]>(`/platforms/${platformId}/sources`),
  createSource: (platformId: string, input: SourceInput) =>
    request<Source>(`/platforms/${platformId}/sources`, { method: 'POST', ...body(input) }),
  updateSource: (id: string, input: Partial<SourceInput> & { lastCursor?: string | null }) =>
    request<Source>(`/sources/${id}`, { method: 'PATCH', ...body(input) }),
  deleteSource: (id: string) => request<{ ok: true }>(`/sources/${id}`, { method: 'DELETE' }),

  keywords: (filter: { platformId?: string; sourceId?: string } = {}) => {
    const params = new URLSearchParams();
    if (filter.platformId) params.set('platformId', filter.platformId);
    if (filter.sourceId) params.set('sourceId', filter.sourceId);
    return request<Keyword[]>(`/keywords${params.size ? `?${params}` : ''}`);
  },
  createKeyword: (input: KeywordInput) =>
    request<Keyword>('/keywords', { method: 'POST', ...body(input) }),
  deleteKeyword: (id: string) => request<{ ok: true }>(`/keywords/${id}`, { method: 'DELETE' }),

  // --- Collecte ---
  /** Lance toutes les sources échues — ce que ferait le cron horaire. */
  runCollection: () =>
    request<{ scheduled: number; mode: 'queue' | 'direct' }>('/collect/run', { method: 'POST' }),
  rescore: (siteId: string, options: { onlyUnscored?: boolean } = {}) =>
    request<{ rescored: number; changed: number; skippedIntel: number; error: string | null }>(
      `/sites/${siteId}/hits/rescore`,
      { method: 'POST', ...body(options) },
    ),
  collect: (sourceId: string) =>
    request<CollectionOutcome>(`/sources/${sourceId}/collect`, { method: 'POST' }),
  backfill: (
    sourceId: string,
    options: {
      since?: string;
      until?: string;
      maxItems?: number;
      notify?: boolean;
      /** `relevance` ignore les dates et prend les meilleurs fils de tous les temps. */
      mode?: 'chronological' | 'relevance';
    },
  ) =>
    request<CollectionOutcome>(`/sources/${sourceId}/backfill`, { method: 'POST', ...body(options) }),
  pollRuns: (sourceId?: string) =>
    request<PollRun[]>(`/poll-runs${sourceId ? `?sourceId=${sourceId}` : ''}`),

  // --- Inbox ---
  hits: (filter: {
    siteId?: string;
    status?: string;
    minScore?: number;
    maxScore?: number;
    sourceId?: string;
    /** Recherche plein texte sur le titre. */
    q?: string;
    /** Bornes sur la date de publication d'origine (ISO 8601). */
    postedSince?: string;
    postedUntil?: string;
    /** `prospect` par défaut : l'inbox n'affiche pas les sources de veille. */
    purpose?: 'prospect' | 'intel' | 'all';
    sort?: 'score' | 'postedAt' | 'discoveredAt' | 'title' | 'status';
    order?: 'asc' | 'desc';
    limit?: number;
    cursor?: string;
  }) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined && value !== '') params.set(key, String(value));
    }
    return request<{ hits: Hit[]; nextCursor: string | null }>(`/hits?${params}`);
  },
  hit: (id: string) => request<{ hit: Hit; drafts: Draft[]; posts: Post[] }>(`/hits/${id}`),
  updateHitStatus: (id: string, status: string) =>
    request<Hit>(`/hits/${id}`, { method: 'PATCH', ...body({ status }) }),
  addManualHit: (input: {
    sourceId: string;
    url: string;
    title: string;
    body?: string;
    postedAt?: string;
  }) => request<Hit>('/hits/manual', { method: 'POST', ...body(input) }),

  // --- Brouillons et publications ---
  createDraft: (hitId: string, instructionsOverride?: string) =>
    request<Draft>('/drafts', { method: 'POST', ...body({ hitId, instructionsOverride }) }),
  updateDraft: (id: string, content: string) =>
    request<Draft>(`/drafts/${id}`, { method: 'PATCH', ...body({ content }) }),
  approveDraft: (id: string) => request<Draft>(`/drafts/${id}/approve`, { method: 'POST' }),

  posts: () => request<PostWithMetrics[]>('/posts'),
  createPost: (input: { hitId: string; draftId?: string; url?: string; notes?: string }) =>
    request<Post>('/posts', { method: 'POST', ...body(input) }),
  updatePost: (id: string, input: { url?: string; notes?: string }) =>
    request<Post>(`/posts/${id}`, { method: 'PATCH', ...body(input) }),
  syncUmami: () =>
    request<{ sitesSynced: number; postsSynced: number; skipped: boolean }>('/posts/sync', {
      method: 'POST',
    }),

  // --- Alertes ---
  channels: () => request<AlertChannel[]>('/alert-channels'),
  createChannel: (input: AlertChannelInput) =>
    request<AlertChannel>('/alert-channels', { method: 'POST', ...body(input) }),
  updateChannel: (id: string, input: Partial<AlertChannelInput>) =>
    request<AlertChannel>(`/alert-channels/${id}`, { method: 'PATCH', ...body(input) }),
  deleteChannel: (id: string) => request<{ ok: true }>(`/alert-channels/${id}`, { method: 'DELETE' }),
  testChannel: (id: string) =>
    request<{ ok: boolean; error?: string }>(`/alert-channels/${id}/test`, { method: 'POST' }),

  // --- Analyse concurrentielle ---
  competitors: (siteId: string) => request<Competitor[]>(`/sites/${siteId}/competitors`),
  createCompetitor: (siteId: string, input: CompetitorInput) =>
    request<Competitor>(`/sites/${siteId}/competitors`, { method: 'POST', ...body(input) }),
  updateCompetitor: (id: string, input: Partial<CompetitorInput>) =>
    request<Competitor>(`/competitors/${id}`, { method: 'PATCH', ...body(input) }),
  deleteCompetitor: (id: string) =>
    request<{ ok: true }>(`/competitors/${id}`, { method: 'DELETE' }),
  analyzeCompetitors: (siteId: string, options: { limit?: number; since?: string } = {}) =>
    request<CompetitorAnalysisRun>(`/sites/${siteId}/competitors/analyze`, {
      method: 'POST',
      ...body(options),
    }),
  competitorInsights: (siteId: string) =>
    request<CompetitorInsight[]>(`/sites/${siteId}/competitors/insights`),

  // --- Studio social ---
  socialCapabilities: () =>
    request<{
      imageProvider: 'openai' | 'gemini' | null;
      storageConfigured: boolean;
      /** Toujours false : aucun adaptateur de publication n'existe. */
      autoPublish: boolean;
    }>('/social/capabilities'),

  socialAccounts: (siteId: string) => request<SocialAccount[]>(`/sites/${siteId}/social/accounts`),
  createSocialAccount: (siteId: string, input: SocialAccountInput) =>
    request<SocialAccount>(`/sites/${siteId}/social/accounts`, { method: 'POST', ...body(input) }),
  updateSocialAccount: (id: string, input: Partial<SocialAccountInput>) =>
    request<SocialAccount>(`/social/accounts/${id}`, { method: 'PATCH', ...body(input) }),
  deleteSocialAccount: (id: string) =>
    request<{ ok: true }>(`/social/accounts/${id}`, { method: 'DELETE' }),

  socialPosts: (filter: { siteId?: string; accountId?: string; status?: string; limit?: number } = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined && value !== '') params.set(key, String(value));
    }
    return request<SocialPostDetail[]>(`/social/posts${params.size ? `?${params}` : ''}`);
  },
  generateSocialPosts: (input: {
    accountId: string;
    brief: string;
    count?: number;
    hitId?: string;
    instructionsOverride?: string;
  }) => request<SocialPostDetail[]>('/social/posts/generate', { method: 'POST', ...body(input) }),
  createSocialPost: (input: { accountId: string; body: string; hashtags?: string[] }) =>
    request<SocialPostDetail>('/social/posts', { method: 'POST', ...body(input) }),
  updateSocialPost: (id: string, input: SocialPostInput) =>
    request<SocialPostDetail>(`/social/posts/${id}`, { method: 'PATCH', ...body(input) }),
  deleteSocialPost: (id: string) =>
    request<{ ok: true }>(`/social/posts/${id}`, { method: 'DELETE' }),
  approveSocialPost: (id: string, scheduledFor?: string | null) =>
    request<SocialPostDetail>(`/social/posts/${id}/approve`, {
      method: 'POST',
      ...body({ scheduledFor }),
    }),
  rejectSocialPost: (id: string, reviewNotes?: string) =>
    request<SocialPostDetail>(`/social/posts/${id}/reject`, {
      method: 'POST',
      ...body({ reviewNotes }),
    }),
  /** Enregistre une publication déjà faite à la main. Ne publie rien. */
  markSocialPostPublished: (id: string, url?: string) =>
    request<SocialPostDetail>(`/social/posts/${id}/published`, { method: 'POST', ...body({ url }) }),
  generateSocialImage: (id: string, input: { prompt?: string; altText?: string } = {}) =>
    request<SocialPostDetail>(`/social/posts/${id}/image`, { method: 'POST', ...body(input) }),
  uploadSocialAsset: (
    siteId: string,
    input: { dataBase64?: string; mimeType?: string; externalUrl?: string; altText?: string },
  ) => request<SocialAsset>(`/sites/${siteId}/social/assets`, { method: 'POST', ...body(input) }),

  /**
   * Les octets du visuel passent par le même `fetch` authentifié que le reste
   * de l'API : un `<img src>` direct n'emporterait pas le cookie de session.
   */
  socialAssetUrl: async (assetId: string): Promise<string> => {
    const response = await fetch(`${BASE}/social/assets/${assetId}/content`, {
      credentials: 'include',
    });
    if (!response.ok) throw new ApiError(response.status, 'Visuel introuvable');
    return URL.createObjectURL(await response.blob());
  },

  // --- Statistiques ---
  stats: (windowDays: number) => request<DashboardStats>(`/stats?windowDays=${windowDays}`),
  timeline: (windowDays: number) =>
    request<{ day: string; discovered: number; kept: number }[]>(
      `/stats/timeline?windowDays=${windowDays}`,
    ),
};
